<script>
  import { onDestroy } from 'svelte';
  import {
    Clock,
    ClipboardList,
    Cloud,
    Check,
    ExternalLink,
    FileText,
    Image,
    Info,
    Link as LinkIcon,
    LoaderCircle,
    Send,
    ShieldCheck,
    UserRound,
    Users
  } from 'lucide-svelte';

  import {
    addGroupMembersWS,
    clearConversationHistoryWS,
    cloudStorage,
    conversations,
    documents,
    dissolveGroupWS,
    getConversationById,
    getDocumentsByConversation,
    leaveConversationWS,
    myId,
    removeGroupMemberWS,
    searchUsersWS,
    transferGroupOwnerWS,
    updateConversationNotificationsWS
  } from '$lib/websocket-client.js';
  import { normalizeUrl, isFile, isImage, isVideo } from '$lib/utils';
  import { api } from '$lib/api.js';
  import { hasLink } from '../../../lib/utils';
  import ConversationActionsSection from './ConversationActionsSection.svelte';
  import ConversationBulletinSection from './ConversationBulletinSection.svelte';
  import ConversationConfirmDialog from './ConversationConfirmDialog.svelte';
  import ConversationContextMenu from './ConversationContextMenu.svelte';
  import ConversationMembersSection from './ConversationMembersSection.svelte';
  import ConversationOverviewSection from './ConversationOverviewSection.svelte';
  import ConversationPreviewModal from './ConversationPreviewModal.svelte';
  import ConversationResourceSection from './ConversationResourceSection.svelte';
  import DetailsSection from './DetailsSection.svelte';
  import MyCloudManager from './MyCloudManager.svelte';

  export let conversationId;
  export let onLeft = () => {};

  const LIMIT = 10;
  const DEFAULT_CLOUD_QUOTA_BYTES = 1024 * 1024 * 1024;
  const DEFAULT_CLOUD_BREAKDOWN = {
    image: { key: 'image', label: 'Ảnh', bytes: 0, count: 0, color: '#ff7a22' },
    video: { key: 'video', label: 'Video', bytes: 0, count: 0, color: '#16a34a' },
    file: { key: 'file', label: 'File', bytes: 0, count: 0, color: '#facc15' },
    audio: { key: 'audio', label: 'Tin nhắn thoại', bytes: 0, count: 0, color: '#3b82f6' }
  };

  let selected = null;
  let leaving = false;
  let muting = false;
  let dissolving = false;
  let clearing = false;
  let leaveError = '';
  let actionError = '';
  let showLeaveConfirm = false;
  let showDissolveConfirm = false;
  let showClearHistoryConfirm = false;
  let memberKeyword = '';
  let memberSuggestions = [];
  let loadingMemberSearch = false;
  let memberSearchError = '';
  let memberActionError = '';
  let memberSearchTimer;
  let memberSearchSequence = 0;
  let lastMemberSearchKey = '';
  let addingMemberId = null;
  let busyMemberId = null;
  let confirmMemberAction = null;
  let longPressTimer;
  let showCloudManager = false;
  let showUpgradeDialog = false;
  let upgradeRequests = [];
  let selectedUpgradePlan = 'standard_100gb';
  let loadingUpgradeRequests = false;
  let sendingUpgradeRequest = false;
  let upgradeError = '';

  let openSections = {
    info: true,
    members: true,
    bulletin: false,
    media: true,
    file: true,
    link: false,
    actions: true
  };

  let showMore = {
    media: false,
    file: false,
    link: false
  };

  let contextMenu = {
    visible: false,
    x: 0,
    y: 0,
    item: null
  };

  $: currentConversation = getConversationById($conversations, conversationId);
  $: conversationType = currentConversation?.type;
  $: isGroup = conversationType === 'group';
  $: isDirect = conversationType === 'direct';
  $: isCloud = conversationType === 'cloud';
  $: cloudData = $cloudStorage || null;
  $: cloudBreakdown = cloudData?.breakdown || DEFAULT_CLOUD_BREAKDOWN;
  $: cloudQuotaBytes = cloudData?.quotaBytes || DEFAULT_CLOUD_QUOTA_BYTES;
  $: cloudUsedBytes = cloudData?.usedBytes || 0;
  $: members = Array.isArray(currentConversation?.memberPreview) ? currentConversation.memberPreview : [];
  $: ownerId = currentConversation?.ownerId;
  $: isCurrentUserOwner = isGroup && String(ownerId || '') === String($myId || '');
  $: memberIds = new Set(members.map((member) => String(member?.id)));
  $: displayMembers = isDirect ? members.filter((member) => String(member?.id) !== String($myId)) : members;
  $: memberSearchQuery = memberKeyword.trim();
  $: visibleMemberSuggestions = memberSuggestions.filter((user) => !memberIds.has(String(user?.id)));
  $: memberCount = Number(currentConversation?.memberCount || members.length || 0);
  $: notificationsMuted = Boolean(currentConversation?.notificationsMuted);
  $: notificationsMutedUntil = currentConversation?.notificationsMutedUntil || null;
  $: notificationsMuteMode = currentConversation?.notificationsMuteMode || 'off';
  $: leaveLabel = isGroup ? 'Thoát nhóm' : 'Thoát hội thoại';
  $: leaveTitle = isGroup ? 'Thoát khỏi nhóm này?' : 'Thoát khỏi hội thoại này?';
  $: leaveDescription = isGroup ? 'Bạn chắc chắn muốn rời khỏi nhóm này?' : 'Bạn chắc chắn muốn thoát hội thoại này?';
  $: memberConfirmTitle = confirmMemberAction?.type === 'transfer' ? 'Trao quyền quản lý?' : 'Mời thành viên rời nhóm?';
  $: memberConfirmDescription =
    confirmMemberAction?.type === 'transfer'
      ? `${confirmMemberAction?.member?.name || 'Thành viên'} sẽ trở thành người quản lý nhóm.`
      : `${confirmMemberAction?.member?.name || 'Thành viên'} sẽ không còn trong nhóm này.`;

  $: conversationId && getDocumentsByConversation(conversationId);
  $: mediaItems = $documents.filter((item) => isMediaDocument(item));
  $: fileItems = $documents.filter((item) => isFileDocument(item));
  $: linkItems = $documents.filter((item) => hasLink(item.url));
  $: if (isCurrentUserOwner && memberSearchQuery !== lastMemberSearchKey) {
    lastMemberSearchKey = memberSearchQuery;
    clearTimeout(memberSearchTimer);
    memberSearchTimer = setTimeout(() => runMemberSearch(), 280);
  }
  $: if (!isCurrentUserOwner && lastMemberSearchKey) {
    resetMemberSearch();
  }

  onDestroy(() => {
    clearTimeout(memberSearchTimer);
    clearTimeout(longPressTimer);
    memberSearchSequence += 1;
  });

  function toggle(section) {
    openSections[section] = !openSections[section];
  }

  function toggleMore(section) {
    showMore[section] = !showMore[section];
  }

  function getInitial(value = '') {
    return (
      String(value || '?')
        .trim()
        .charAt(0)
        .toUpperCase() || '?'
    );
  }

  function formatBytes(value = 0) {
    const bytes = Number(value || 0);
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let index = 0;
    while (size >= 1024 && index < units.length - 1) {
      size /= 1024;
      index += 1;
    }
    const digits = size >= 10 || index < 2 ? 0 : 1;
    return `${size.toFixed(digits)} ${units[index]}`;
  }

  function usageWidth(bytes = 0) {
    const quota = Number(cloudQuotaBytes || 0);
    if (!quota) return '0%';
    return `${Math.max(0, Math.min(100, (Number(bytes || 0) / quota) * 100))}%`;
  }

  async function openUpgradeDialog() {
    showUpgradeDialog = true;
    upgradeError = '';
    loadingUpgradeRequests = true;
    try {
      const response = await api.get('/api/my-cloud/upgrade-requests');
      upgradeRequests = response.requests || [];
    } catch (error) {
      upgradeError = error?.message || 'Không thể tải yêu cầu nâng dung lượng.';
    } finally {
      loadingUpgradeRequests = false;
    }
  }

  async function submitUpgradeRequest() {
    if (sendingUpgradeRequest) return;
    sendingUpgradeRequest = true;
    upgradeError = '';
    try {
      const response = await api.post('/api/my-cloud/upgrade-requests', { planCode: selectedUpgradePlan });
      upgradeRequests = [response.request, ...upgradeRequests];
    } catch (error) {
      upgradeError = error?.message || 'Không thể gửi yêu cầu nâng dung lượng.';
    } finally {
      sendingUpgradeRequest = false;
    }
  }

  function upgradeStatus(status) {
    return ({ pending: 'Chờ tiếp nhận', reviewing: 'Đang xử lý', approved: 'Đã duyệt', rejected: 'Từ chối', cancelled: 'Đã hủy' })[status] || status;
  }

  function updateCloudSummary(event) {
    if (event?.detail?.cloud) cloudStorage.set(event.detail.cloud);
  }

  function isMediaDocument(item) {
    const mime = item?.mime || '';
    return isImage(item?.url, mime) || isVideo(item?.url, mime);
  }

  function isFileDocument(item) {
    const mime = item?.mime || '';
    if (item?.category === 'files') return true;
    if (isMediaDocument(item) || hasLink(item?.url || '')) return false;
    return isFile(item?.url || '', mime);
  }

  function isOwnerMember(member) {
    return member?.role === 'owner' || String(member?.id) === String(ownerId);
  }

  function getMemberSubtitle(member) {
    if (isGroup) {
      const role = isOwnerMember(member) ? 'Trưởng nhóm' : 'Thành viên';
      return String(member?.id) === String($myId) ? `Bạn · ${role}` : role;
    }

    return String(member?.id) === String($myId) ? 'Bạn' : 'Liên hệ';
  }

  function resetMemberSearch() {
    clearTimeout(memberSearchTimer);
    memberSearchSequence += 1;
    lastMemberSearchKey = '';
    memberKeyword = '';
    memberSuggestions = [];
    loadingMemberSearch = false;
    memberSearchError = '';
  }

  async function runMemberSearch() {
    const searchKeyword = memberKeyword.trim();
    const seq = ++memberSearchSequence;

    if (!isCurrentUserOwner || searchKeyword.length < 1) {
      memberSuggestions = [];
      loadingMemberSearch = false;
      memberSearchError = '';
      return;
    }

    loadingMemberSearch = true;
    memberSearchError = '';

    try {
      const rows = await searchUsersWS({
        keyword: searchKeyword,
        scope: 'system',
        limit: 12,
        excludeExisting: false
      });

      if (seq !== memberSearchSequence) return;
      memberSuggestions = rows.filter((user) => !memberIds.has(String(user?.id)));
    } catch (error) {
      if (seq !== memberSearchSequence) return;
      memberSearchError = error?.message || 'Không thể tìm thành viên.';
    } finally {
      if (seq === memberSearchSequence) {
        loadingMemberSearch = false;
      }
    }
  }

  async function addMember(user) {
    if (!conversationId || !user?.id || addingMemberId || busyMemberId) return;

    try {
      addingMemberId = user.id;
      memberActionError = '';
      await addGroupMembersWS(conversationId, [user.id]);
      resetMemberSearch();
    } catch (error) {
      memberActionError = error?.message || 'Không thể thêm thành viên.';
    } finally {
      addingMemberId = null;
    }
  }

  function handleMemberKeywordChange(event) {
    memberKeyword = event.currentTarget.value;
  }

  function requestRemoveMember(member) {
    if (!member?.id || busyMemberId) return;
    memberActionError = '';
    confirmMemberAction = { type: 'remove', member };
  }

  function requestTransferOwner(member) {
    if (!member?.id || busyMemberId) return;
    memberActionError = '';
    confirmMemberAction = { type: 'transfer', member };
  }

  async function confirmMemberManagementAction() {
    const action = confirmMemberAction;
    const member = action?.member;
    if (!conversationId || !member?.id || busyMemberId) return;

    try {
      busyMemberId = member.id;
      memberActionError = '';

      if (action.type === 'transfer') {
        await transferGroupOwnerWS(conversationId, member.id);
      } else {
        await removeGroupMemberWS(conversationId, member.id);
      }

      confirmMemberAction = null;
    } catch (error) {
      memberActionError = error?.message || 'Không thể cập nhật thành viên.';
    } finally {
      busyMemberId = null;
    }
  }

  function openPreview(item) {
    selected = item;
  }

  function closePreview() {
    selected = null;
  }

  async function updateNotificationState(mode, durationMinutes = null) {
    if (!conversationId || muting || leaving || dissolving) return;

    try {
      muting = true;
      actionError = '';
      await updateConversationNotificationsWS(conversationId, {
        mode,
        durationMinutes
      });
    } catch (error) {
      actionError = error?.message || 'Không thể cập nhật thông báo.';
    } finally {
      muting = false;
    }
  }

  function handleMutePreset(preset) {
    updateNotificationState(preset.mode, preset.durationMinutes);
  }

  function handleUnmute() {
    updateNotificationState('off');
  }

  function openContextMenu(event, item) {
    event.preventDefault();

    const padding = 8;
    const menuWidth = 176;
    const menuHeight = 48;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let x = event.clientX;
    let y = event.clientY;

    if (x + menuWidth > viewportWidth) x = viewportWidth - menuWidth - padding;
    if (y + menuHeight > viewportHeight) y = viewportHeight - menuHeight - padding;

    contextMenu = { visible: true, x, y, item };
  }

  function handleTouchStart(event, item) {
    longPressTimer = setTimeout(() => {
      const touch = event.touches[0];
      contextMenu = {
        visible: true,
        x: touch.clientX,
        y: touch.clientY,
        item
      };
    }, 500);
  }

  function handleTouchEnd() {
    clearTimeout(longPressTimer);
  }

  function closeContextMenu() {
    contextMenu.visible = false;
  }

  function jumpToMessageFromDocument(item) {
    if (!item?.id) return;

    window.dispatchEvent(
      new CustomEvent('search-jump-to-message', {
        detail: {
          messageId: item.id,
          conversationId,
          keyword: ''
        }
      })
    );
  }

  function viewOriginalMessage() {
    jumpToMessageFromDocument(contextMenu.item);
    closeContextMenu();
  }

  async function confirmLeave() {
    if (!conversationId || leaving) return;

    try {
      leaving = true;
      leaveError = '';
      actionError = '';
      await leaveConversationWS(conversationId);
      showLeaveConfirm = false;
      onLeft?.();
    } catch (error) {
      leaveError = error?.message || 'Không thể thoát hội thoại.';
    } finally {
      leaving = false;
    }
  }

  async function confirmDissolve() {
    if (!conversationId || dissolving) return;

    try {
      dissolving = true;
      actionError = '';
      await dissolveGroupWS(conversationId);
      showDissolveConfirm = false;
      onLeft?.();
    } catch (error) {
      actionError = error?.message || 'Không thể giải tán nhóm.';
    } finally {
      dissolving = false;
    }
  }

  async function confirmClearHistory() {
    if (!conversationId || clearing) return;

    try {
      clearing = true;
      actionError = '';
      await clearConversationHistoryWS(conversationId);
      showClearHistoryConfirm = false;
    } catch (error) {
      actionError = error?.message || 'Không thể xoá lịch sử trò chuyện.';
    } finally {
      clearing = false;
    }
  }
</script>

<div class="flex h-full w-full flex-col bg-white text-[13px] text-slate-900">
  <header class="border-b border-slate-200 bg-white px-4 py-3">
    <p class="text-[10px] font-semibold uppercase tracking-[0.08em] text-blue-600">Thông tin</p>
    <h2 class="mt-1 truncate text-sm font-semibold text-slate-950">
      {currentConversation?.title || 'Hội thoại'}
    </h2>
  </header>

  {#if currentConversation}
    <main class="min-h-0 flex-1 overflow-y-auto">
      {#if isCloud}
        <section class="border-b border-slate-200 px-5 py-8 text-center">
          <div class="mx-auto grid h-16 w-16 place-items-center rounded-full border border-blue-100 bg-blue-600 text-white shadow-sm">
            <Cloud size={34} strokeWidth={2.1} />
          </div>
          <h3 class="mt-5 text-xl font-semibold text-slate-950">My Documents</h3>
          <p class="mx-auto mt-3 max-w-[18rem] text-sm leading-6 text-slate-500">
            Lưu trữ và truy cập nhanh những nội dung quan trọng của bạn ngay trên My Cloud.
          </p>
        </section>

        <section class="space-y-3 border-b border-slate-200 bg-slate-50 px-4 py-4">
          <div class="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-100">
            <div class="flex items-center justify-between gap-3">
              <strong class="text-sm font-semibold text-slate-950">Dung lượng lưu trữ</strong>
              <span class="text-sm font-semibold text-slate-950">
                {formatBytes(cloudUsedBytes)} / {formatBytes(cloudQuotaBytes)}
              </span>
            </div>

            <div class="mt-4 flex h-4 overflow-hidden rounded-md bg-slate-200">
              {#each Object.values(cloudBreakdown) as item (item.key)}
                {#if item.bytes > 0}
                  <span
                    class="h-full border-r border-white/70 last:border-r-0"
                    style={`width: ${usageWidth(item.bytes)}; background: ${item.color};`}>
                  </span>
                {/if}
              {/each}
            </div>

            <div class="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 text-[12px] text-slate-500">
              {#each Object.values(cloudBreakdown) as item (item.key)}
                <span class="inline-flex min-w-0 items-center gap-1.5">
                  <i class="h-2.5 w-2.5 shrink-0 rounded-full" style={`background: ${item.color};`}></i>
                  <span class="truncate">{item.label}</span>
                </span>
              {/each}
            </div>
          </div>

          <button type="button" class="w-full rounded-lg bg-slate-200 px-3 py-2.5 text-sm font-semibold text-slate-950 hover:bg-slate-300" onclick={() => (showCloudManager = true)}>
            Xem và dọn dẹp My Documents
          </button>

          <div class="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-100">
            <div class="flex items-start gap-3">
              <Info size={18} class="mt-0.5 shrink-0 text-blue-600" />
              <div class="min-w-0">
                <strong class="block text-sm font-semibold text-slate-950">Nâng cấp dung lượng My Documents</strong>
                <p class="mt-2 text-sm leading-6 text-slate-600">
                  Mở rộng dung lượng lên đến 100GB và tự động bảo toàn dữ liệu trò chuyện với zCloud.
                </p>
                <button type="button" class="mt-3 rounded-md bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700 hover:bg-blue-100" onclick={openUpgradeDialog}>
                  Thêm dung lượng
                </button>
              </div>
            </div>
          </div>
        </section>

        <section class="border-b border-slate-200">
          <div class="flex items-center gap-3 px-4 py-4 text-sm font-medium text-slate-900">
            <Clock size={20} />
            <span>Danh sách nhắc hẹn</span>
          </div>
        </section>
      {:else}
        <DetailsSection
          title="Thông tin hội thoại"
          icon={FileText}
          open={openSections.info}
          onToggle={() => toggle('info')}>
          <ConversationOverviewSection
            conversation={currentConversation}
            {isGroup}
            {isDirect}
            memberCount={memberCount || displayMembers.length} />
        </DetailsSection>
      {/if}

      {#if isGroup || isDirect}
        <DetailsSection
          title={isGroup ? 'Quản lý thành viên' : 'Người trò chuyện'}
          icon={Users}
          open={openSections.members}
          onToggle={() => toggle('members')}>
          <ConversationMembersSection
            {isGroup}
            {isCurrentUserOwner}
            members={displayMembers}
            memberCount={memberCount || displayMembers.length}
            {memberKeyword}
            memberSuggestions={visibleMemberSuggestions}
            {memberSearchQuery}
            {loadingMemberSearch}
            {memberSearchError}
            {memberActionError}
            {addingMemberId}
            {busyMemberId}
            {getInitial}
            {getMemberSubtitle}
            {isOwnerMember}
            onKeywordChange={handleMemberKeywordChange}
            onAddMember={addMember}
            onTransferOwner={requestTransferOwner}
            onRemoveMember={requestRemoveMember} />
        </DetailsSection>
      {/if}

      {#if isGroup}
        <DetailsSection
          title="Tiện ích"
          icon={ClipboardList}
          open={openSections.bulletin}
          onToggle={() => toggle('bulletin')}>
          <ConversationBulletinSection {conversationId} />
        </DetailsSection>
      {/if}

      <DetailsSection title="Ảnh / Video" icon={Image} open={openSections.media} onToggle={() => toggle('media')}>
        <ConversationResourceSection
          items={mediaItems}
          emptyText="Chưa có ảnh hoặc video được chia sẻ."
          limit={LIMIT}
          showMore={showMore.media}
          renderMode="media"
          {normalizeUrl}
          isImage={(url, mime = '') => isImage(url, mime)}
          onOpen={openPreview}
          onToggleMore={() => toggleMore('media')}
          onContextMenu={openContextMenu}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd} />
      </DetailsSection>

      <DetailsSection title="File" icon={FileText} open={openSections.file} onToggle={() => toggle('file')}>
        <ConversationResourceSection
          icon={FileText}
          items={fileItems}
          emptyText="Chưa có file được chia sẻ."
          limit={LIMIT}
          showMore={showMore.file}
          renderMode="file"
          onOpen={openPreview}
          onToggleMore={() => toggleMore('file')}
          onContextMenu={openContextMenu}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd} />
      </DetailsSection>

      <DetailsSection title="Link" icon={LinkIcon} open={openSections.link} onToggle={() => toggle('link')}>
        <ConversationResourceSection
          icon={ExternalLink}
          items={linkItems}
          emptyText="Chưa có link được chia sẻ."
          limit={LIMIT}
          showMore={showMore.link}
          renderMode="link"
          {normalizeUrl}
          onToggleMore={() => toggleMore('link')}
          onContextMenu={openContextMenu}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd} />
      </DetailsSection>

      {#if isGroup || isDirect}
        <DetailsSection
          title="Quản lý hội thoại"
          icon={ShieldCheck}
          open={openSections.actions}
          onToggle={() => toggle('actions')}>
          <ConversationActionsSection
            {isGroup}
            {isCurrentUserOwner}
            {leaveLabel}
            {leaveError}
            {actionError}
            {notificationsMuted}
            {notificationsMuteMode}
            {notificationsMutedUntil}
            {muting}
            {leaving}
            {dissolving}
            {clearing}
            onMutePreset={handleMutePreset}
            onUnmute={handleUnmute}
            onClearHistory={() => {
              leaveError = '';
              actionError = '';
              showClearHistoryConfirm = true;
            }}
            onLeave={() => {
              leaveError = '';
              actionError = '';
              showLeaveConfirm = true;
            }}
            onDissolve={() => {
              leaveError = '';
              actionError = '';
              showDissolveConfirm = true;
            }} />
        </DetailsSection>
      {/if}
    </main>
  {:else}
    <div class="grid flex-1 place-items-center px-6 text-center text-sm text-slate-500">
      <div>
        <UserRound size={26} class="mx-auto mb-3 text-slate-400" />
        <p>Chọn một hội thoại để xem thông tin.</p>
      </div>
    </div>
  {/if}
</div>

<ConversationPreviewModal item={selected} onClose={closePreview} />

{#if showCloudManager && isCloud}
  <MyCloudManager
    {conversationId}
    on:close={() => (showCloudManager = false)}
    on:updated={updateCloudSummary} />
{/if}

{#if showUpgradeDialog}
  <div class="fixed inset-0 z-[110] flex items-stretch justify-center bg-slate-950/45 p-0 backdrop-blur-[2px] sm:items-center sm:p-5" role="presentation">
    <section class="flex h-full w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl sm:max-h-[90vh] sm:rounded-xl" role="dialog" aria-modal="true" aria-labelledby="my-cloud-upgrade-title">
      <header class="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4">
        <div>
          <p class="text-[11px] font-semibold uppercase tracking-wider text-blue-600">2k Cloud</p>
          <h2 id="my-cloud-upgrade-title" class="mt-1 text-lg font-semibold text-slate-950">Yêu cầu thêm dung lượng</h2>
        </div>
        <button type="button" aria-label="Đóng" class="grid h-9 w-9 place-items-center rounded-full text-slate-500 hover:bg-slate-100" onclick={() => (showUpgradeDialog = false)}>
          <X size={19} />
        </button>
      </header>

      <div class="min-h-0 flex-1 space-y-6 overflow-y-auto p-5">
        <div>
          <h3 class="text-sm font-semibold text-slate-900">Chọn gói đăng ký</h3>
          <div class="mt-3 grid gap-3 sm:grid-cols-2">
            {#each [
              { id: 'standard_100gb', name: 'Tiêu chuẩn', capacity: '100 GB', price: '390.000đ / năm', detail: 'Dành cho nhu cầu lưu trữ cá nhân.' },
              { id: 'family_500gb', name: 'Gia đình', capacity: '500 GB', price: '1.490.000đ / năm', detail: 'Dung lượng lớn cho nhiều thiết bị và nội dung.' }
            ] as plan (plan.id)}
              <button type="button" class={`rounded-lg border p-4 text-left transition-colors ${selectedUpgradePlan === plan.id ? 'border-blue-500 bg-blue-50/60 ring-1 ring-blue-500' : 'border-slate-200 hover:border-slate-300'}`} aria-pressed={selectedUpgradePlan === plan.id} onclick={() => (selectedUpgradePlan = plan.id)}>
                <span class="flex items-start justify-between gap-2">
                  <span>
                    <strong class="block text-base font-semibold text-slate-950">{plan.name}</strong>
                    <span class="mt-1 block text-sm text-slate-500">{plan.capacity}</span>
                  </span>
                  {#if selectedUpgradePlan === plan.id}<Check size={19} class="text-blue-600" />{/if}
                </span>
                <span class="mt-4 block text-lg font-bold text-blue-700">{plan.price}</span>
                <span class="mt-1 block text-xs leading-5 text-slate-500">{plan.detail}</span>
              </button>
            {/each}
          </div>
          <p class="mt-3 text-xs leading-5 text-slate-500">Đây là yêu cầu tư vấn từ 2k. Chưa phát sinh thanh toán hoặc tự động thay đổi quota.</p>
          {#if upgradeError}<p class="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{upgradeError}</p>{/if}
          <button type="button" class="mt-4 inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60" disabled={sendingUpgradeRequest} onclick={submitUpgradeRequest}>
            {#if sendingUpgradeRequest}<LoaderCircle size={16} class="animate-spin" />{:else}<Send size={16} />{/if}
            Gửi yêu cầu
          </button>
        </div>

        <section class="border-t border-slate-200 pt-5">
          <div class="flex items-center justify-between gap-3">
            <h3 class="text-sm font-semibold text-slate-900">Yêu cầu của bạn</h3>
            <span class="text-xs text-slate-500">{upgradeRequests.length} yêu cầu</span>
          </div>
          {#if loadingUpgradeRequests}
            <p class="py-6 text-center text-sm text-slate-500">Đang tải...</p>
          {:else if upgradeRequests.length === 0}
            <p class="py-6 text-center text-sm text-slate-500">Chưa có yêu cầu nào.</p>
          {:else}
            <div class="mt-3 divide-y divide-slate-100">
              {#each upgradeRequests as request (request.id)}
                <div class="flex items-center justify-between gap-3 py-3">
                  <div>
                    <p class="text-sm font-medium text-slate-900">{request.plan_code === 'family_500gb' ? 'Gia đình · 500 GB' : 'Tiêu chuẩn · 100 GB'}</p>
                    <p class="mt-1 text-xs text-slate-500">2k · {new Intl.NumberFormat('vi-VN').format(request.annual_price_vnd || 0)}đ/năm · {new Date(request.created_at).toLocaleString('vi-VN')}</p>
                  </div>
                  <span class="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">{upgradeStatus(request.status)}</span>
                </div>
              {/each}
            </div>
          {/if}
        </section>
      </div>
    </section>
  </div>
{/if}

<ConversationContextMenu
  visible={contextMenu.visible}
  x={contextMenu.x}
  y={contextMenu.y}
  onClose={closeContextMenu}
  onViewOriginal={viewOriginalMessage} />

<ConversationConfirmDialog
  open={showLeaveConfirm}
  title={leaveTitle}
  description={leaveDescription}
  confirmLabel="Xác nhận"
  tone="danger"
  loading={leaving}
  onCancel={() => (showLeaveConfirm = false)}
  onConfirm={confirmLeave} />

<ConversationConfirmDialog
  open={showDissolveConfirm}
  title="Giải tán nhóm này?"
  description="Nhóm sẽ biến mất khỏi danh sách của tất cả thành viên hiện tại."
  confirmLabel="Giải tán nhóm"
  tone="danger"
  loading={dissolving}
  onCancel={() => (showDissolveConfirm = false)}
  onConfirm={confirmDissolve} />

<ConversationConfirmDialog
  open={showClearHistoryConfirm}
  title="Xoá lịch sử trò chuyện?"
  description="Toàn bộ tin nhắn trong hội thoại này sẽ bị xoá và khung chat sẽ trở về trạng thái trống."
  confirmLabel="Xoá lịch sử"
  tone="danger"
  loading={clearing}
  onCancel={() => (showClearHistoryConfirm = false)}
  onConfirm={confirmClearHistory} />

<ConversationConfirmDialog
  open={!!confirmMemberAction}
  title={memberConfirmTitle}
  description={memberConfirmDescription}
  confirmLabel="Xác nhận"
  tone={confirmMemberAction?.type === 'remove' ? 'danger' : 'neutral'}
  loading={!!busyMemberId}
  onCancel={() => (confirmMemberAction = null)}
  onConfirm={confirmMemberManagementAction} />
